import { useEffect, useRef, useState, type ReactElement } from 'react';
import type { SavedDeckSelectionRef } from '../../server/protocol.js';
import { Alert, Button, DelayedLoader } from '../ui/kit/index.js';
import type {
  FastOnboardingProgress,
  OnboardingApi,
  OnboardingPresetDto,
} from './api.js';
import {
  claimOperationId,
  clearClaimOperation,
  type OnboardingStorage,
} from './storage.js';

export interface OnboardingPresetPickerProps {
  presets: readonly OnboardingPresetDto[];
  csrfToken: string;
  api: OnboardingApi;
  storage?: OnboardingStorage;
  onClaimed: (
    progress: FastOnboardingProgress,
    deck: SavedDeckSelectionRef,
  ) => void;
}

export function OnboardingPresetPicker({
  presets,
  csrfToken,
  api,
  storage = globalThis.sessionStorage,
  onClaimed,
}: OnboardingPresetPickerProps): ReactElement {
  const starter = presets[0];
  const [error, setError] = useState<string>();
  const [attempt, setAttempt] = useState(0);
  const controllerRef = useRef<AbortController | undefined>(undefined);

  useEffect(() => {
    if (starter === undefined || controllerRef.current !== undefined) return undefined;
    const controller = new AbortController();
    controllerRef.current = controller;
    setError(undefined);
    void (async () => {
      try {
        const operationId = claimOperationId(storage);
        const result = await api.claimPreset(
          starter.id,
          starter.revision,
          operationId,
          csrfToken,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        clearClaimOperation(storage);
        onClaimed(result.progress, {
          deckId: result.deck.id,
          revision: result.deck.revision,
        });
      } catch (reason) {
        if (!controller.signal.aborted) {
          setError(reason instanceof Error
            ? `Не удалось сохранить первую колоду: ${reason.message}`
            : 'Не удалось сохранить первую колоду.');
        }
      } finally {
        if (controllerRef.current === controller) controllerRef.current = undefined;
      }
    })();
    return () => {
      controller.abort();
      if (controllerRef.current === controller) controllerRef.current = undefined;
    };
  }, [api, attempt, csrfToken, onClaimed, starter, storage]);

  if (starter === undefined) {
    return <main className="onboarding-loading" role="alert">Учебная колода недоступна.</main>;
  }

  if (error === undefined) {
    return <main data-testid="onboarding-starter-provisioning"><DelayedLoader /></main>;
  }

  return (
    <main className="onboarding-loading" role="alert" data-testid="onboarding-starter-error">
      <div>
        <Alert>{error}</Alert>
        <Button variant="secondary" size="sm" onClick={() => setAttempt((value) => value + 1)}>
          Повторить
        </Button>
      </div>
    </main>
  );
}
